/**
 * useSpeechRecognition — thin React wrapper over the Web Speech API
 * (Wave 2 voice input). Capability-detected and Norwegian-first (`nb-NO`).
 *
 * Powers the InputRow mic button: a single-utterance dictation that streams
 * interim results into the textarea so the user sees words appear as they
 * speak, then reviews and sends via the existing onSubmit → SSE path. We do
 * NOT auto-send — for a kids-first, allergy-adjacent assistant, a human glance
 * before send is the safe default.
 *
 * Support reality: `SpeechRecognition` / `webkitSpeechRecognition` exist on
 * desktop Chrome and desktop Safari, but NOT on iOS Safari / installed iOS
 * PWAs (the kitchen tablet's Chromium and a desktop browser get voice; iPhones
 * fall back to typing). `supported` is false there and the caller shows an
 * "ikke støttet" hint rather than a dead button.
 *
 * Lifecycle: one recognition instance per hook, created lazily on first
 * `start()` and torn down on unmount. `continuous = false` so a natural pause
 * ends the utterance; `interimResults = true` for live feedback.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ── Minimal Web Speech typings (the DOM lib does not ship these) ──────────────
interface SpeechRecognitionAlternative {
  readonly transcript: string;
}
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  readonly [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  readonly [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionEventLike extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: string;
}
interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface UseSpeechRecognitionOptions {
  lang?: string;
  /** Called on each result; `isFinal` marks the settled transcript for the utterance. */
  onResult: (transcript: string, isFinal: boolean) => void;
  /** Called with the raw Web Speech error code (e.g. 'not-allowed', 'no-speech'). */
  onError?: (code: string) => void;
}

export interface UseSpeechRecognition {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
}

export function useSpeechRecognition({
  lang = 'nb-NO',
  onResult,
  onError,
}: UseSpeechRecognitionOptions): UseSpeechRecognition {
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  // Keep the latest callbacks without re-creating the recognition instance.
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  onResultRef.current = onResult;
  onErrorRef.current = onError;

  const supported = getCtor() !== null;

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (recRef.current) return; // already listening
    const Ctor = getCtor();
    if (!Ctor) return;

    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const result = e.results[i]!;
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) final += text;
        else interim += text;
      }
      if (final) onResultRef.current(final, true);
      else if (interim) onResultRef.current(interim, false);
    };
    rec.onerror = (e) => {
      onErrorRef.current?.(e.error);
    };
    rec.onend = () => {
      recRef.current = null;
      setListening(false);
    };

    recRef.current = rec;
    setListening(true);
    rec.start();
  }, [lang]);

  // Tear down a live recognition on unmount.
  useEffect(() => {
    return () => {
      recRef.current?.abort();
      recRef.current = null;
    };
  }, []);

  return { supported, listening, start, stop };
}
